/**
 * components/profile/PriorityMigrationWizard.jsx — v3.7.9 Phase 84.31 Part 3
 *
 * One-time wizard for users who answered the old "pick up to three"
 * priorities question. The new model is:
 *
 *   • primary_priority   — the single priority the dashboard emphasises
 *   • secondary_priority — optional supporting priority
 *
 * The wizard shows the user's legacy picks as radio choices for the
 * primary, then a smaller list for an optional secondary. On confirm
 * we POST /api/profile/migrate-priorities which:
 *   • Writes primary_priority + secondary_priority into the JSONB bag
 *   • Archives any remaining legacy picks in `legacy_priorities`
 *   • Removes the old `top_intents` field so this modal never fires again
 *   • Dual-writes to life_profile_facts (silent if unmigrated)
 *
 * Renders itself null when the parent doesn't ship a
 * `summary.priorities_migration.needed` signal — so it's safe to keep
 * mounted permanently.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Human labels for the legacy intent values. Kept in sync with the
// options in lib/lifeProfile.js — any unknown key falls back to its
// slug-cased value so the wizard is defensive against catalog drift.
const INTENT_LABELS = {
  debt_free:              'Become debt-free',
  emergency_fund:         'Build an emergency fund',
  buy_home:               'Buy a home',
  kids_education:         "Save for children's education",
  retire_early:           'Make work optional earlier',
  financial_independence: 'Reach financial independence',
  reduce_stress:          'Reduce money stress',
  grow_investments:       'Grow long-term investments',
  legacy:                 'Leave a legacy',
  in_control:             'Feel more in control each month',
};

function label(key) {
  return INTENT_LABELS[key] || (typeof key === 'string'
    ? key.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    : String(key));
}

export default function PriorityMigrationWizard({ summary, onCompleted }) {
  const migration = summary?.priorities_migration;
  const legacy = useMemo(
    () => Array.isArray(migration?.legacy_intents) ? migration.legacy_intents.filter(Boolean) : [],
    [migration],
  );

  const [open, setOpen]         = useState(true);
  const [primary, setPrimary]   = useState(legacy[0] || '');
  const [secondary, setSecondary] = useState(legacy[1] || '');
  const [saving, setSaving]     = useState(false);

  if (!migration?.needed || legacy.length === 0) return null;

  const secondaryOptions = legacy.filter((k) => k !== primary);

  const submit = async () => {
    if (!primary) {
      toast.error('Please pick your primary priority.');
      return;
    }
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await axios.post(
        `${BACKEND_URL}/api/profile/migrate-priorities`,
        { primary, secondary: secondary || null },
        { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 15000 },
      );
      if (!r.data?.ok) throw new Error(r.data?.error || 'Migration failed');
      toast.success('Priorities updated.');
      setOpen(false);
      onCompleted?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    // "Not now" — dismisses the wizard for the current tab open. It'll
    // reappear on next load until the user picks a primary, because
    // the backend still has legacy top_intents on the row.
    setOpen(false);
    onCompleted?.({ skipped: true });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent
        className="sm:max-w-lg rounded-2xl"
        data-testid="priority-migration-wizard"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">
            Choose your top priority
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground leading-relaxed">
            2120 used to ask you to pick three priorities. We&rsquo;ve simplified: one
            <span className="font-medium text-charcoal"> primary priority</span> shapes
            your dashboard, and an optional secondary keeps you honest. Anything else
            you picked stays archived — nothing is lost.
          </p>

          {/* Primary picker */}
          <fieldset className="space-y-1.5" data-testid="priority-migration-primary">
            <legend className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-1">
              Primary priority
            </legend>
            {legacy.map((k) => {
              const isSel = primary === k;
              return (
                <label
                  key={k}
                  htmlFor={`prio-primary-${k}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                    isSel
                      ? 'border-primary bg-primary/5'
                      : 'border-border/70 hover:border-primary/50 hover:bg-muted/40'
                  }`}
                >
                  <input
                    id={`prio-primary-${k}`}
                    type="radio"
                    name="prio-primary"
                    className="h-4 w-4 accent-current text-primary"
                    checked={isSel}
                    onChange={() => {
                      setPrimary(k);
                      if (secondary === k) setSecondary('');
                    }}
                    data-testid={`priority-migration-primary-${k}`}
                  />
                  <span className={isSel ? 'font-medium text-charcoal' : 'text-charcoal'}>
                    {label(k)}
                  </span>
                </label>
              );
            })}
          </fieldset>

          {/* Secondary picker */}
          {secondaryOptions.length > 0 && (
            <fieldset className="space-y-1.5 pt-1" data-testid="priority-migration-secondary">
              <legend className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-1">
                Optional secondary priority
              </legend>
              <label
                htmlFor="prio-secondary-none"
                className={`flex items-center gap-3 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                  !secondary
                    ? 'border-primary bg-primary/5'
                    : 'border-border/70 hover:border-primary/50 hover:bg-muted/40'
                }`}
              >
                <input
                  id="prio-secondary-none"
                  type="radio"
                  name="prio-secondary"
                  className="h-4 w-4 accent-current text-primary"
                  checked={!secondary}
                  onChange={() => setSecondary('')}
                  data-testid="priority-migration-secondary-none"
                />
                <span className="text-muted-foreground">No secondary priority</span>
              </label>
              {secondaryOptions.map((k) => {
                const isSel = secondary === k;
                return (
                  <label
                    key={k}
                    htmlFor={`prio-secondary-${k}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                      isSel
                        ? 'border-primary bg-primary/5'
                        : 'border-border/70 hover:border-primary/50 hover:bg-muted/40'
                    }`}
                  >
                    <input
                      id={`prio-secondary-${k}`}
                      type="radio"
                      name="prio-secondary"
                      className="h-4 w-4 accent-current text-primary"
                      checked={isSel}
                      onChange={() => setSecondary(k)}
                      data-testid={`priority-migration-secondary-${k}`}
                    />
                    <span className={isSel ? 'font-medium text-charcoal' : 'text-charcoal'}>
                      {label(k)}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={skip}
            disabled={saving}
            data-testid="priority-migration-skip"
          >
            Not now
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !primary}
            data-testid="priority-migration-save"
          >
            {saving ? 'Saving…' : 'Confirm priorities'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
